import { useTranslation } from 'react-i18next';
import { GetServerSidePropsContext } from 'next';
import { useEffect, useMemo } from 'react';
import { Format } from '../../graphql/generated/api-graphql';
import { Configuration } from '../../graphql/Services';
import format from '../../graphql/Services/Cheese/queries/format';
import { isMaintenance, set503Header, setDefaultCacheHeader, setNoCacheHeader, ssrPersistedQuery, transformSeoToMetas } from '../../helpers/SSRHelpers';
import { initWithLangAndLabels } from '../../i18n';
import routes from '../../routes/definitions';
import Router from '../../routes/Router';
import FormatTemplate from '../../templates/FormatTemplate';
import { GetServerSidePropsReturnType, PageProps } from '../../relay/Common/PageProps';
import ProductFormat from '../../components/molecules/ProductFormat';
import productPopinTransform from '../../relay/ProductPopin/transform';
import BugsnagHelpers from '../../helpers/BugsnagHelpers';
import BreadcrumbWrapper from '../../relay/Breadcrumb/BreadcrumbWrapper';
import HeaderWrapper from '../../relay/Header/HeaderWrapper';
import FooterWrapper from '../../relay/Footer/FooterWrapper';
import tracking, { TrackingContentGroup } from '../../tracking';
export type FormatPageProps = PageProps & {
  data: Format;
};
export const FormatPage = ({
  data,
  configuration
}: FormatPageProps) => {
  const {
    t
  } = useTranslation();
  useEffect(() => {
    tracking.page({
      brand: undefined,
      group: TrackingContentGroup.FORMAT,
      page: data?.title,
      section: undefined,
      hideRubrique: true
    });
  }, [data?.title]);
  const breadcrumbEntries = useMemo(() => [data.mainBrand ? {
    label: data.mainBrand?.name,
    href: Router.getRouteUrl(routes.brand, {
      slug: data.mainBrand?.slug
    })
  } : {}, {
    label: data?.title
  }], [data]);
  const productFormatProps = productPopinTransform({
    t,
    data: {
      format: data
    },
    displayMoreButton: false
  });
  return <FormatTemplate breadcrumb={<BreadcrumbWrapper entries={breadcrumbEntries} />} header={<HeaderWrapper data={configuration?.header} socials={configuration?.footer?.socialLinks} />} footer={<FooterWrapper data={configuration?.footer} />}>
      {productFormatProps && <ProductFormat {...productFormatProps} />}
    </FormatTemplate>;
};
export async function getServerSideProps(context: GetServerSidePropsContext): GetServerSidePropsReturnType<FormatPageProps> {
  try {
    const slug = context?.query?.slug;
    const [rs, configurationRs] = await Promise.all([ssrPersistedQuery(format, {
      slug
    }), ssrPersistedQuery(Configuration.queries.configuration)]);
    const {
      data
    } = rs ?? {};
    const configuration = configurationRs?.data ?? null;
    const i18n = initWithLangAndLabels('fr', {});
    if (!configuration || rs?.errors) {
      set503Header(context);
      return {
        props: {
          i18n,
          error: true
        }
      };
    }
    if (isMaintenance(configuration)) {
      set503Header(context);
    } else {
      setDefaultCacheHeader(context);
    }
    if (!data) {
      return {
        notFound: true,
        props: {
          i18n,
          configuration
        }
      };
    }
    const seo = {
      title: data?.title,
      description: data?.body,
      ...data?.seo
    };
    return {
      props: {
        i18n,
        configuration,
        metas: transformSeoToMetas(Router.getRouteUrl(routes.format, {
          slug
        }), seo),
        data
      }
    };
  } catch (e: any) {
    BugsnagHelpers?.notify(e, event => {
      const {
        resolvedUrl,
        query,
        params
      } = context;
      event.addMetadata('context', {
        resolvedUrl,
        query,
        params
      });
    });
    setNoCacheHeader(context);
    return {
      notFound: true
    };
  }
}
export default FormatPage;